// Record a whole trip in one pass: several travel COSTS (each becomes an
// `expenses` row) plus any number of billable travel-TIME legs (each becomes a
// real `time_entries` row, priced from the Global → Client → Project cascade).
//
// Nothing here is a new storage concept — it composes POST /api/expenses and
// POST /api/time-entries. Recording travel time as genuine time entries is what
// buys approval, the retainer debit, and the unbilled-time invoice path for
// free; storing hours on the expense row would have bypassed all three.
//
// Trip identity is carried in each expense's `notes` ("<purpose> — <label>"),
// not a new column, so the flat Expenses list still reads as a trip.

import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';

import type { ServiceRate } from '@stfors/shared-types';
import { Modal } from './Modal';
import { IconAction } from './IconAction';
import { DateInput } from './DateInput';
import { MoneyInput } from './MoneyInput';
import { LookupSelect } from './LookupSelect';
import { VendorPicker } from './VendorPicker';
import { FileDrop } from './FileDrop';
import { ProjectOptions } from './ProjectOptions';
import { TimeRangeFields, type TimeRangeValue } from './TimeRangeFields';
import { useProjects } from '../api/queries/projects';
import { useCreateExpense } from '../api/queries/expenses';
import { useUploadDocument } from '../api/queries/documents';
import { useLookups, activeOptionsFor } from '../api/queries/lookups';
import { useSystemSettings } from '../api/queries/settings';
import {
  useServiceRates,
  useCreateTimeEntry,
  effectiveRates,
} from '../api/queries/time-entries';
import { ApiError } from '../api/client';
import { can } from '../auth/store';
import { formatMoney } from '../utils/money';
import { tracksMileage } from '../utils/expense';
import { endFromDuration } from '../utils/duration';
import {
  buildReceiptFilename,
  extensionOf,
  normalizeReceiptFile,
  previewReceiptFilename,
  renameFile,
  receiptMime,
  type ReceiptFilenameInput,
} from '../utils/receipt';
import { ReceiptIcon } from './ReceiptIcon';
import { MatterSelect } from './MatterSelect';
import { DocumentPreviewModal, type PreviewableFile } from './DocumentPreviewModal';
import './NewTravelExpenseModal.css';
import { Toggle } from './Toggle';

/** Sentinel for the "general business expense" option — sent to the API as
 *  null. Same convention as NewExpensePage. */
const GENERAL_BUSINESS = -1 as const;

/** NUMERIC(12,2) / NUMERIC(8,2) wire format shared by amount, tax and mileage. */
const MONEY_RE = /^\d+(\.\d{1,2})?$/;

/** Default start-of-travel time of day when the modal opens (09:00 local). */
const DEFAULT_START_HOUR = 9;

/** Matches the documents route's 50MB raw-body cap with room to spare. */
const MAX_RECEIPT_BYTES = 25 * 1024 * 1024;
const RECEIPT_ACCEPT = 'image/*,application/pdf';

const MS_PER_HOUR = 3_600_000;

interface CostRow {
  /** Stable React key — rows are added/removed, so the index won't do. */
  key: string;
  type: string;
  /** Short per-cost note, appended to the trip purpose in `notes`. */
  label: string;
  /** Item subtotal, exclusive of tax (the API stores subtotal + tax). */
  amount: string;
  taxAmount: string;
  vendorId: number | null;
  /** A name-only vendor — a `generic_vendor` category ("Gas station") or a
   *  one-off the user typed. Mutually exclusive with `vendorId`; sent as the
   *  API's free-text `vendor` snapshot so no vendor record is created. */
  vendorText: string;
  /** Display name of a PICKED vendor — kept only so the receipt filename can
   *  name it; the expense carries the vendor by id. */
  vendorName: string;
  /** Schedule C-style category for year-end reporting (`tax_category` lookup).
   *  Per row, not per trip: a trip's lodging and its meals deduct differently,
   *  and that 50% split is exactly the distinction worth capturing. */
  taxCategory: string;
  /** The receipt's own number. Sent as `receipt_number` and — more visibly —
   *  the third segment of the stored filename. */
  receiptNumber: string;
  miles: string;
  receipt: File | null;
  /** Vault path once the receipt has uploaded — survives a retry so a
   *  re-submit never re-uploads the same file. */
  receiptPath: string | null;
  /** OCR progress 0..1 while this row's receipt is being read, null when idle.
   *  Never gates Save. */
  ocrPct: number | null;
  /** Which of this row's fields OCR filled, so the user can see and undo it. */
  ocrFilled: string[];
  /** Set once this row's expense exists. Guards against double-recording
   *  when a later row fails and the user presses Save again. */
  savedId: number | null;
  error: string | null;
}

/** One leg of the trip — "travel to", "travel from", a mid-trip drive.
 *  Each becomes its own time entry, so each carries its own rate, span and
 *  description (they bill as separate invoice lines). */
interface TimeRow {
  key: string;
  serviceRateId: number;
  range: TimeRangeValue;
  description: string;
  /** Once the user edits the description we stop re-seeding it from the
   *  trip purpose. */
  descTouched: boolean;
  savedId: number | null;
  error: string | null;
}

let rowSeq = 0;
function blankCostRow(): CostRow {
  rowSeq += 1;
  return {
    key: `cost-${rowSeq}`,
    type: '',
    label: '',
    amount: '',
    taxAmount: '',
    vendorId: null,
    vendorText: '',
    vendorName: '',
    taxCategory: '',
    receiptNumber: '',
    miles: '',
    receipt: null,
    receiptPath: null,
    ocrPct: null,
    ocrFilled: [],
    savedId: null,
    error: null,
  };
}

function startOfDay(ms: number): number {
  const d = new Date(ms);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** Move `instant` onto `day`'s calendar date, keeping its time of day. */
function anchorToDay(instant: number, day: number): number {
  const i = new Date(instant);
  const d = new Date(day);
  return new Date(
    d.getFullYear(), d.getMonth(), d.getDate(),
    i.getHours(), i.getMinutes(), 0, 0,
  ).getTime();
}

// Positional default: the second leg of a trip is nearly always the way back.
// Purely a starting point — the field is editable, and an edit sticks.
function seedDescription(purpose: string, index: number): string {
  const base = index === 1 ? 'Return travel' : 'Travel';
  const p = purpose.trim();
  return p.length > 0 ? `${base} — ${p}` : base;
}

/** Re-apply the positional description defaults to every untouched leg.
 *  Run after add/remove too, or a removal leaves stale "Return travel". */
function reseedDescriptions(rows: TimeRow[], purpose: string): TimeRow[] {
  return rows.map((t, i) => (
    t.descTouched ? t : { ...t, description: seedDescription(purpose, i) }
  ));
}

/** A new leg picks up where the previous one ended and reuses its rate, so
 *  adding the return trip is one click plus a time tweak. */
function blankTimeRow(prev: TimeRow | undefined, tripDate: number | null): TimeRow {
  rowSeq += 1;
  let start = (tripDate ?? startOfDay(Date.now())) + DEFAULT_START_HOUR * MS_PER_HOUR;
  if (prev && prev.range.start !== null) {
    // Falls all the way back to the previous leg's START when that leg is still
    // blank — `endFromDuration` returns its input unmoved for a non-numeric
    // duration, which is now the shape a freshly added leg has.
    start = prev.range.end ?? endFromDuration(prev.range.start, prev.range.hours);
  }
  return {
    key: `time-${rowSeq}`,
    serviceRateId: prev?.serviceRateId ?? 0,
    // End and duration start EMPTY — a seeded six-minute leg was a placeholder
    // that had to be cleared before it could be replaced. Only the start is
    // worth guessing. Matches Quick add and the Log Time page.
    range: { start, end: null, hours: '' },
    description: 'Travel',
    descTouched: false,
    savedId: null,
    error: null,
  };
}

function isMoney(v: string): boolean {
  return MONEY_RE.test(v.trim()) && Number(v) >= 0;
}

/** The naming inputs a cost row contributes, shared by the preview shown in the
 *  row and the name actually sent on upload — so what you see is what lands. */
function receiptNameInput(
  r: CostRow,
  caseNumber: string | null,
  tripDate: number | null,
): Omit<ReceiptFilenameInput, 'ext'> {
  return {
    caseNumber,
    dateEpoch: tripDate,
    receiptNumber: r.receiptNumber.trim() || null,
    vendor: r.vendorText.trim() || r.vendorName,
    type: r.type,
  };
}

/** Trip purpose + per-cost label, joined into the expense's notes. */
function composeNotes(purpose: string, label: string): string | undefined {
  const parts = [purpose.trim(), label.trim()].filter((s) => s.length > 0);
  return parts.length > 0 ? parts.join(' — ') : undefined;
}

function rateOptionText(r: ServiceRate): string {
  const scope = r.project_override_for
    ? ' (project rate)'
    : r.client_override_for
      ? ' (client rate)'
      : '';
  return `${r.name}${scope} — ${formatMoney(r.amount)}/${r.rate_type === 'hourly' ? 'hr' : 'flat'}`;
}

export interface NewTravelExpenseModalProps {
  onClose: () => void;
  /** Seed the project dropdown from the list's current filter. `'none'`
   *  preselects general business. */
  initialProjectId?: number | 'none';
}

export function NewTravelExpenseModal({
  onClose,
  initialProjectId,
}: NewTravelExpenseModalProps): JSX.Element {
  const createExpense = useCreateExpense();
  const createTimeEntry = useCreateTimeEntry();
  const uploadDoc = useUploadDocument();
  const projectsQ = useProjects();
  const lookupsQ = useLookups('expense_type');
  // The generic-vendor → expense-type map lives in system settings.
  const settingsQ = useSystemSettings();

  const [projectId, setProjectId] = useState<number>(
    initialProjectId === 'none'
      ? GENERAL_BUSINESS
      : typeof initialProjectId === 'number' && initialProjectId > 0
        ? initialProjectId
        : 0,
  );
  // The project's matter the whole trip belongs to (0061) — every cost and
  // travel-time leg is saved on it.
  const [matterId, setMatterId] = useState<number | null>(null);
  const [purpose, setPurpose] = useState('');
  const [tripDate, setTripDate] = useState<number | null>(() => startOfDay(Date.now()));
  // Travel is normally passed through to the client, so this starts on —
  // unlike /expenses/new, where most expenses are the firm's own cost.
  const [reimbursable, setReimbursable] = useState(true);
  const [rows, setRows] = useState<CostRow[]>(
    () => [blankCostRow(), blankCostRow(), blankCostRow()],
  );

  // Starts with one leg, so the default is "you ARE billing travel time" —
  // forgetting the hours is the thing this modal exists to prevent. Removing
  // the last leg is how you say "costs only"; there's no separate toggle.
  const [timeRows, setTimeRows] = useState<TimeRow[]>(
    () => [blankTimeRow(undefined, startOfDay(Date.now()))],
  );

  const [topError, setTopError] = useState<string | null>(null);
  /** An attached-but-not-yet-uploaded receipt, opened from its row. */
  const [preview, setPreview] = useState<PreviewableFile | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const projects = projectsQ.data?.projects ?? [];
  const realProjectId = projectId > 0 ? projectId : undefined;
  const ratesQ = useServiceRates(realProjectId);
  const rates = useMemo(
    () => effectiveRates(ratesQ.data?.service_rates ?? []),
    [ratesQ.data],
  );

  const typeOptions = activeOptionsFor(lookupsQ.data?.lookup_options, 'expense_type');
  const genericVendorTypes = settingsQ.data?.settings.generic_vendor_types ?? {};

  // Seed the three starter cost rows' types from the live lookup. The seeded
  // categories are admin-renameable (migration 0037), so match loosely and
  // leave the row blank rather than writing a value that isn't in the list.
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current) return;
    const options = activeOptionsFor(lookupsQ.data?.lookup_options, 'expense_type');
    if (options.length === 0) return;
    seeded.current = true;
    // `/travel/` before `/mileage/` matters now that "Travel (track mileage)"
    // exists: options come back in sort order, so plain Travel (10) is found
    // first and the starter row doesn't demand an odometer reading.
    const pick = (re: RegExp): string =>
      options.find((o) => re.test(o.value) || re.test(o.label ?? ''))?.value ?? '';
    const wanted = [pick(/travel/i), pick(/lodg|hotel/i), pick(/meal|food/i)];
    setRows((prev) => prev.map((r, i) => {
      if (r.type !== '' || !wanted[i]) return r;
      // Remember what we chose, so type inference can tell an untouched row
      // from one the user set deliberately.
      seededTypeRef.current.set(r.key, wanted[i]!);
      return { ...r, type: wanted[i]! };
    }));
  }, [lookupsQ.data]);

  // Moving the trip date drags the legs onto the new day, keeping each one's
  // time of day. Only legs still sitting on the OLD trip date move: a leg the
  // user deliberately pushed to another day (the return of a multi-day trip)
  // stays where they put it.
  const prevTripDate = useRef(tripDate);
  useEffect(() => {
    const prev = prevTripDate.current;
    prevTripDate.current = tripDate;
    if (tripDate === null || prev === null || prev === tripDate) return;
    setTimeRows((prevRows) => prevRows.map((t) => {
      if (t.range.start === null || startOfDay(t.range.start) !== prev) return t;
      const anchored = anchorToDay(t.range.start, tripDate);
      // SHIFT the end by the same delta rather than rebuilding it from the
      // duration. `hours` is snapped to a tenth, so `endFromDuration` would
      // quietly round an entered end by up to three minutes every time the
      // trip date changed — the same rewrite <TimeRangeFields> stopped doing.
      // Moving a leg to another day must not re-time it.
      const delta = anchored - t.range.start;
      return {
        ...t,
        range: {
          ...t.range,
          start: anchored,
          end: t.range.end === null ? null : t.range.end + delta,
        },
      };
    }));
  }, [tripDate]);

  // Auto-select ONLY a travel-named hourly rate. Falling back to "the first
  // rate on the sheet" the way the log-time form does would silently bill
  // drive time at the examination rate — here the user must choose instead.
  useEffect(() => {
    if (rates.length === 0) return;
    const travel = rates.find((r) => r.rate_type === 'hourly' && /travel/i.test(r.name));
    if (!travel) return;
    setTimeRows((prev) => (
      prev.some((t) => t.serviceRateId === 0)
        ? prev.map((t) => (t.serviceRateId === 0 ? { ...t, serviceRateId: travel.id } : t))
        : prev
    ));
  }, [rates]);

  // Keep untouched leg descriptions in step with the trip purpose.
  useEffect(() => {
    setTimeRows((prev) => reseedDescriptions(prev, purpose));
  }, [purpose]);

  const showTimeSection = projectId > 0 && can('time_entry.write');

  /**
   * A generic vendor's expense type, per the admin-managed map (Settings →
   * Expense types from receipts). Null when the vendor isn't a generic, or when
   * the mapped type is no longer an active lookup value — an admin can rename
   * either side, and inferring a type that isn't on the dropdown would leave
   * the row unsubmittable with no explanation.
   */
  const genericTypeFor = (vendor: string): string | null => {
    const mapped = genericVendorTypes[vendor.trim()];
    if (!mapped) return null;
    return typeOptions.some((o) => o.value === mapped) ? mapped : null;
  };

  /**
   * The type each row was seeded with, so inference can tell "untouched" from
   * "the user chose this". A ref, not state: it's bookkeeping about the past
   * and must never trigger a render.
   */
  const seededTypeRef = useRef<Map<string, string>>(new Map());

  // Drives the receipt-name preview in each row; the submit path recomputes it
  // from the same helper so what the row shows is what gets stored.
  const caseNumber = projectId > 0
    ? (projects.find((p) => p.id === projectId)?.case_number ?? null)
    : null;

  const rateFor = (id: number): ServiceRate | undefined => rates.find((r) => r.id === id);

  const costsTotal = rows.reduce((acc, r) => {
    const item = Number(r.amount);
    const tax = Number(r.taxAmount);
    return acc
      + (Number.isFinite(item) && r.amount.trim() !== '' ? item : 0)
      + (Number.isFinite(tax) && r.taxAmount.trim() !== '' ? tax : 0);
  }, 0);
  const timeTotal = showTimeSection
    ? timeRows.reduce((acc, t) => {
      const rate = rateFor(t.serviceRateId);
      return acc + (rate && MONEY_RE.test(t.range.hours)
        ? Number(t.range.hours) * Number(rate.amount)
        : 0);
    }, 0)
    : 0;

  const patchRow = (key: string, patch: Partial<CostRow>): void => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };
  const patchTimeRow = (key: string, patch: Partial<TimeRow>): void => {
    setTimeRows((prev) => prev.map((t) => (t.key === key ? { ...t, ...patch } : t)));
  };

  // Latest rows for the async OCR callbacks, so a value typed while a receipt
  // was being read still wins (same guard <AddressFields> uses for an in-flight
  // geocode). Reading `rows` from the closure would see the state as it was
  // when the file was chosen, seconds earlier.
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  /** Attach one file to one cost row and start reading it. */
  const attachReceipt = (key: string, file: File): void => {
    if (file.size > MAX_RECEIPT_BYTES) {
      patchRow(key, { receipt: null, error: 'Receipt must be under 25 MB.' });
      return;
    }
    patchRow(key, { receipt: file, receiptPath: null, error: null });
    void runRowOcr(key, file);
  };

  /**
   * A drop anywhere on the modal that isn't on a row's own zone. You come back
   * from a trip holding four receipts, so they spread across the rows that
   * don't have one yet — in order, adding rows when they run out. Dropping onto
   * a specific row still overrides that and targets exactly that row.
   */
  const spreadReceipts = (files: File[]): void => {
    const usable = files.filter((f) => f.size <= MAX_RECEIPT_BYTES);
    if (usable.length === 0) {
      setTopError('Receipts must be under 25 MB each.');
      return;
    }
    setTopError(usable.length < files.length
      ? `${files.length - usable.length} file(s) skipped — receipts must be under 25 MB.`
      : null);

    // Built from rowsRef rather than a setRows updater, because the assignments
    // are needed OUT here to start OCR on them — and rowsRef won't reflect the
    // new rows until the next render, so reading it back afterwards would find
    // nothing.
    const next = [...rowsRef.current];
    const assigned: Array<{ key: string; file: File }> = [];
    let from = 0;
    for (const file of usable) {
      // Reuse an open row before creating one — the modal starts with three.
      let slot = next.findIndex(
        (r, idx) => idx >= from && r.savedId === null && r.receipt === null,
      );
      if (slot < 0) {
        next.push(blankCostRow());
        slot = next.length - 1;
      }
      next[slot] = { ...next[slot]!, receipt: file, receiptPath: null, error: null };
      assigned.push({ key: next[slot]!.key, file });
      from = slot + 1;
    }
    setRows(next);
    for (const a of assigned) void runRowOcr(a.key, a.file);
  };

  /** Read one row's receipt and fill only that row's BLANK fields. */
  const runRowOcr = async (key: string, file: File): Promise<void> => {
    patchRow(key, { ocrPct: 0, ocrFilled: [] });
    try {
      const { readReceipt } = await import('../utils/receipt-ocr');
      const parsed = await readReceipt(file, (p) => patchRow(key, { ocrPct: p }));
      const cur = rowsRef.current.find((r) => r.key === key);
      if (!parsed || !cur || cur.savedId !== null) return;

      const patch: Partial<CostRow> = {};
      const filled: string[] = [];
      if (parsed.total.value !== null && cur.amount.trim() === '') {
        // This modal's Amount is the item subtotal, with sales tax beside it —
        // so back the tax out of the receipt's TOTAL when one was found.
        const tax = parsed.tax.value === null ? 0 : Number(parsed.tax.value);
        patch.amount = Math.max(0, Number(parsed.total.value) - tax).toFixed(2);
        filled.push('amount');
      }
      if (parsed.tax.value !== null && cur.taxAmount.trim() === '') {
        patch.taxAmount = parsed.tax.value;
        filled.push('tax');
      }
      if (parsed.receiptNumber.value !== null && cur.receiptNumber.trim() === '') {
        patch.receiptNumber = parsed.receiptNumber.value;
        filled.push('receipt #');
      }
      if (parsed.vendor.value !== null && cur.vendorText.trim() === '' && cur.vendorId === null) {
        patch.vendorText = parsed.vendor.value;
        filled.push('vendor');
      }
      // A generic vendor implies what kind of expense this is — "Parking" is
      // travel, "Hotel / lodging" is lodging. The mapping is admin-managed
      // (Settings → Expense types from receipts) rather than guessed from
      // keywords. Only applied while the row's type is untouched: a type you
      // picked yourself always wins.
      const inferred = patch.vendorText ? genericTypeFor(patch.vendorText) : null;
      if (inferred !== null && cur.type === seededTypeRef.current.get(key)) {
        patch.type = inferred;
        filled.push('type');
      }
      // Not the date: it belongs to the trip, not to one of its costs.
      patchRow(key, { ...patch, ocrFilled: filled });
    } finally {
      patchRow(key, { ocrPct: null });
    }
  };

  const clearRowOcr = (key: string): void => {
    const cur = rowsRef.current.find((r) => r.key === key);
    if (!cur) return;
    const patch: Partial<CostRow> = { ocrFilled: [] };
    if (cur.ocrFilled.includes('amount')) patch.amount = '';
    if (cur.ocrFilled.includes('tax')) patch.taxAmount = '';
    if (cur.ocrFilled.includes('vendor')) patch.vendorText = '';
    if (cur.ocrFilled.includes('receipt #')) patch.receiptNumber = '';
    if (cur.ocrFilled.includes('type')) patch.type = seededTypeRef.current.get(key) ?? '';
    patchRow(key, patch);
  };

  const busy = submitting || createExpense.isPending || createTimeEntry.isPending
    || uploadDoc.isPending;

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setTopError(null);

    if (projectId === 0) {
      setTopError('Pick a project, or choose "General business".');
      return;
    }
    if (tripDate === null) {
      setTopError('Pick the trip date.');
      return;
    }

    // ---- validate the cost rows -------------------------------------------
    // A row counts as filled once it has an amount; blank rows are ignored so
    // the three starter rows never force the user to delete what they skipped.
    const working = rows.map((r) => ({ ...r, error: null as string | null }));
    let invalid = false;
    for (const r of working) {
      if (r.savedId !== null || r.amount.trim() === '') continue;
      if (r.type.trim() === '') {
        r.error = 'Pick a type.';
      } else if (!isMoney(r.amount) || Number(r.amount) <= 0) {
        r.error = 'Enter an amount greater than zero.';
      } else if (r.taxAmount.trim() !== '' && !isMoney(r.taxAmount)) {
        r.error = 'Sales tax must be zero or more.';
      } else if (tracksMileage(r.type) && r.miles.trim() !== '' && !isMoney(r.miles)) {
        r.error = 'Miles must be zero or more.';
      }
      if (r.error) invalid = true;
    }
    const toCreate = working.filter((r) => r.savedId === null && r.amount.trim() !== '');

    // ---- validate the travel legs -----------------------------------------
    const workingTime = timeRows.map((t) => ({ ...t, error: null as string | null }));
    const timeToCreate = showTimeSection
      ? workingTime.filter((t) => t.savedId === null)
      : [];
    for (const t of timeToCreate) {
      if (t.serviceRateId <= 0) {
        t.error = 'Pick a service rate.';
      } else if (t.range.start === null) {
        t.error = 'Pick a start time.';
      } else if (t.range.end !== null && t.range.end <= t.range.start) {
        t.error = 'End time must be after the start time.';
      } else if (!MONEY_RE.test(t.range.hours) || Number(t.range.hours) <= 0) {
        t.error = 'Enter a duration greater than zero.';
      } else if (t.description.trim().length === 0) {
        t.error = 'Describe this leg — it appears on the invoice.';
      }
      if (t.error) invalid = true;
    }

    if (invalid) {
      setRows(working);
      setTimeRows(workingTime);
      setTopError('Please fix the highlighted rows.');
      return;
    }

    if (toCreate.length === 0 && timeToCreate.length === 0) {
      setTopError('Add at least one cost, or one leg of travel time.');
      return;
    }

    const projectIdForApi: number | null =
      projectId === GENERAL_BUSINESS ? null : projectId;
    const already = rows.filter((r) => r.savedId !== null).length;

    setSubmitting(true);
    try {
      // ---- 1. upload receipts, before anything is created ------------------
      // A failed upload aborts the whole submit rather than silently dropping
      // the receipt (same contract as /expenses/new).
      for (const r of toCreate) {
        if (r.receipt === null || r.receiptPath !== null) continue;
        try {
          // Re-encode anything the API's pdfkit can't embed, then name it —
          // the vault stores a UUID, so X-Filename is the only place a human
          // name can land.
          const normalized = await normalizeReceiptFile(r.receipt);
          const named = renameFile(normalized, buildReceiptFilename({
            ...receiptNameInput(r, caseNumber, tripDate),
            ext: extensionOf(normalized),
          }));
          const resp = await uploadDoc.mutateAsync({
            file: named,
            document_type: 'other',
            convert_to_pdf: true,
            ...(projectIdForApi !== null ? { project_id: projectIdForApi } : {}),
          });
          r.receiptPath = resp.document.file_path;
        } catch (err) {
          r.error = err instanceof ApiError
            ? `Receipt upload failed (${err.status}).`
            : 'Receipt upload failed.';
          setRows(working);
          setTopError('Nothing was recorded — a receipt failed to upload.');
          return;
        }
      }

      // ---- 2. one expense per filled row ----------------------------------
      let created = 0;
      for (const r of toCreate) {
        const item = Number(r.amount);
        const tax = r.taxAmount.trim() === '' ? 0 : Number(r.taxAmount);
        try {
          const resp = await createExpense.mutateAsync({
            project_id: projectIdForApi,
            type: r.type,
            // The API's `amount` is the total paid; tax is broken out as well.
            amount: (item + tax).toFixed(2),
            tax_amount: tax.toFixed(2),
            date: tripDate,
            vendor_id: r.vendorId,
            // Only reaches the API when no vendor was picked — a vendor_id
            // makes the server re-snapshot the real name over anything here.
            vendor: r.vendorId === null ? (r.vendorText.trim() || undefined) : undefined,
            receipt_number: r.receiptNumber.trim() || undefined,
            tax_category: r.taxCategory.trim() || undefined,
            reimbursable: reimbursable || undefined,
            mileage: tracksMileage(r.type) && r.miles.trim() !== ''
              ? Number(r.miles).toFixed(2)
              : undefined,
            notes: composeNotes(purpose, r.label),
            receipt_path: r.receiptPath ?? undefined,
            matter_id: projectIdForApi === null ? null : matterId,
          });
          r.savedId = resp.expense.id;
          created += 1;
        } catch (err) {
          r.error = err instanceof ApiError && err.status === 403
            ? "You don't have permission to record expenses on this project."
            : 'Could not record this cost.';
          setRows(working);
          setTopError(
            `${already + created} of ${already + toCreate.length} costs saved — fix the `
            + 'highlighted cost and press Save again. Saved rows are skipped.',
          );
          return;
        }
      }
      setRows(working);

      // ---- 3. one time entry per travel leg -------------------------------
      let timeCreated = 0;
      for (const t of timeToCreate) {
        if (projectIdForApi === null || t.range.start === null) continue;
        try {
          const resp = await createTimeEntry.mutateAsync({
            project_id: projectIdForApi,
            service_rate_id: t.serviceRateId,
            hours: t.range.hours.trim(),
            date: t.range.start,
            description: t.description.trim(),
            matter_id: matterId,
            // Both-or-neither: only record the span when an end was entered.
            ...(t.range.end !== null
              ? { started_at_epoch: t.range.start, ended_at_epoch: t.range.end }
              : {}),
          });
          t.savedId = resp.time_entry.id;
          timeCreated += 1;
        } catch (err) {
          t.error = err instanceof ApiError && err.status === 403
            ? "You don't have permission to log time on this project."
            : 'Could not log this leg.';
          setTimeRows(workingTime);
          const costNote = already + toCreate.length > 0 ? 'The costs were saved, but ' : '';
          setTopError(
            `${costNote}${timeCreated} of ${timeToCreate.length} travel legs were logged — `
            + 'fix the highlighted leg and press Save again. Saved rows are skipped.',
          );
          return;
        }
      }
      setTimeRows(workingTime);

      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const typeOptionsLoading = lookupsQ.isPending;

  return (
    <Modal
      title="New Travel"
      open
      onClose={onClose}
      busy={busy}
      size="lg"
      footer={
        <>
          <div className="ntm-totals">
            <span>Costs <strong>{formatMoney(costsTotal.toFixed(2))}</strong></span>
            {showTimeSection && timeRows.length > 0 && (
              <span>
                Travel time <strong>{formatMoney(timeTotal.toFixed(2))}</strong>
              </span>
            )}
            <span className="ntm-grand">
              Total <strong>{formatMoney((costsTotal + timeTotal).toFixed(2))}</strong>
            </span>
          </div>
          <IconAction action="cancel" onClick={onClose} disabled={busy} />
          <button type="submit" form="ntm-form" className="btn-cyan-primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save trip'}
          </button>
        </>
      }
    >
      <form id="ntm-form" className="ntm-form" onSubmit={onSubmit} noValidate>
        {/* ---- trip ---- */}
        <div className="ntm-row">
          <label className="ntm-field">
            <span>Project *</span>
            <select
              className="form-input"
              value={projectId}
              onChange={(e) => {
                setProjectId(Number(e.target.value));
                setMatterId(null); // a matter belongs to one project
                // Re-derive every leg's rate from the new project's sheet.
                setTimeRows((prev) => prev.map((t) => (
                  t.savedId === null ? { ...t, serviceRateId: 0 } : t
                )));
              }}
              disabled={busy || projectsQ.isPending}
              required
            >
              <option value={0}>
                {projectsQ.isPending ? 'Loading projects…' : 'Select a project…'}
              </option>
              <option value={GENERAL_BUSINESS}>— General business (no project) —</option>
              <ProjectOptions projects={projects} />
            </select>
          </label>

          <label className="ntm-field">
            <span>Trip date *</span>
            <DateInput value={tripDate} onChange={setTripDate} disabled={busy} required />
          </label>
        </div>

        <MatterSelect
          className="ntm-field"
          projectId={projectId > 0 ? projectId : null}
          value={matterId}
          onChange={setMatterId}
          disabled={busy}
        />

        <label className="ntm-field">
          <span>Purpose</span>
          <input
            className="form-input"
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            disabled={busy}
            maxLength={200}
            placeholder="e.g. Richmond deposition"
          />
          <span className="ntm-hint">
            Written into every cost's notes so the trip stays readable on the Expenses list.
          </span>
        </label>

        <Toggle
          checked={reimbursable}
          onChange={setReimbursable}
          label="Reimbursable to client — applies to every cost below"
          disabled={busy}
          className="ntm-field ntm-check"
        />

        {/* ---- costs ---- */}
        <div className="ntm-section">
          <h3 className="ntm-section-title">Costs</h3>
          <p className="ntm-hint">
            Each cost with an amount becomes its own expense. Leave a row blank to skip it.
          </p>

          {/* The bulk path: you come home from a trip holding several receipts
              and drop them all at once. Each lands on the next row without one,
              gets read, and the amounts fill themselves in. Dropping onto a
              single row's own zone still targets exactly that row. */}
          <FileDrop
            accept={RECEIPT_ACCEPT}
            multiple
            disabled={busy}
            label="Drop receipts here"
            hint="One per cost — images and PDFs, up to 25 MB each. They're read automatically."
            onFiles={spreadReceipts}
          />

          {rows.map((r) => {
            const locked = busy || r.savedId !== null;
            return (
              <div
                key={r.key}
                className={`ntm-cost${r.savedId !== null ? ' ntm-saved' : ''}${r.error ? ' ntm-invalid' : ''}`}
              >
                <div className="ntm-cost-main">
                  <label className="ntm-field ntm-f-type" htmlFor={`${r.key}-type`}>
                    <span>Type</span>
                    <LookupSelect
                      id={`${r.key}-type`}
                      category="expense_type"
                      value={r.type}
                      onChange={(v) => patchRow(r.key, { type: v })}
                      disabled={locked || typeOptionsLoading}
                      allowEmpty
                      emptyLabel={typeOptionsLoading ? 'Loading…' : 'Select type…'}
                    />
                  </label>

                  {/* The state field is still `label` — it's what composeNotes
                      joins onto the trip purpose — but "Notes" is what it is. */}
                  <label className="ntm-field ntm-f-label" htmlFor={`${r.key}-label`}>
                    <span>Notes</span>
                    <input
                      id={`${r.key}-label`}
                      className="form-input"
                      value={r.label}
                      onChange={(e) => patchRow(r.key, { label: e.target.value })}
                      disabled={locked}
                      maxLength={120}
                      placeholder="Hampton Inn folio"
                    />
                  </label>

                  <label className="ntm-field ntm-f-money" htmlFor={`${r.key}-amount`}>
                    <span>Amount</span>
                    <MoneyInput
                      id={`${r.key}-amount`}
                      value={r.amount}
                      onChange={(v) => patchRow(r.key, { amount: v })}
                      disabled={locked}
                      placeholder="0.00"
                    />
                  </label>

                  <label className="ntm-field ntm-f-money" htmlFor={`${r.key}-tax`}>
                    <span>Sales tax</span>
                    <MoneyInput
                      id={`${r.key}-tax`}
                      value={r.taxAmount}
                      onChange={(v) => patchRow(r.key, { taxAmount: v })}
                      disabled={locked}
                      placeholder="0.00"
                    />
                  </label>

                  <div className="ntm-cost-actions">
                    {r.savedId !== null ? (
                      <span className="ntm-saved-pill" title="Already recorded">saved</span>
                    ) : (
                      <IconAction
                        action="delete"
                        label="Remove this cost"
                        iconSize={16}
                        disabled={busy || rows.length === 1}
                        onClick={() => setRows((prev) => prev.filter((x) => x.key !== r.key))}
                      />
                    )}
                  </div>
                </div>

                <div className={`ntm-cost-extra${tracksMileage(r.type) ? ' ntm-cost-extra-miles' : ''}`}>
                  <label className="ntm-field ntm-f-vendor">
                    <span>Vendor</span>
                    {/* Most travel spend is a gas station or a lunch you'll
                        never deal with again — allowGeneric lets the row carry
                        a category instead of enrolling a vendor. */}
                    <VendorPicker
                      value={r.vendorId}
                      onChange={(id, vendor) =>
                        patchRow(r.key, {
                          vendorId: id,
                          vendorName: vendor?.name ?? '',
                          // The vendor's own default fills the category, but
                          // never over a choice already made for this row —
                          // same rule as /expenses/new and the edit modal.
                          ...(vendor?.default_tax_category && r.taxCategory === ''
                            ? { taxCategory: vendor.default_tax_category }
                            : {}),
                        })}
                      allowGeneric
                      textValue={r.vendorText}
                      onTextChange={(name) => patchRow(r.key, { vendorText: name })}
                      canAdd={can('vendor.write')}
                      disabled={locked}
                    />
                  </label>

                  <label className="ntm-field ntm-f-taxcat" htmlFor={`${r.key}-taxcat`}>
                    <span>Tax category</span>
                    <LookupSelect
                      id={`${r.key}-taxcat`}
                      category="tax_category"
                      value={r.taxCategory}
                      onChange={(v) => patchRow(r.key, { taxCategory: v })}
                      disabled={locked}
                      allowEmpty
                    />
                  </label>

                  {tracksMileage(r.type) && (
                    <label className="ntm-field ntm-f-miles" htmlFor={`${r.key}-miles`}>
                      <span>Miles</span>
                      <input
                        id={`${r.key}-miles`}
                        className="form-input"
                        value={r.miles}
                        onChange={(e) => patchRow(r.key, { miles: e.target.value })}
                        disabled={locked}
                        inputMode="decimal"
                        placeholder="e.g. 142.0"
                      />
                    </label>
                  )}

                  <label className="ntm-field ntm-f-rcptno" htmlFor={`${r.key}-rcptno`}>
                    <span>Receipt #</span>
                    <input
                      id={`${r.key}-rcptno`}
                      className="form-input"
                      value={r.receiptNumber}
                      onChange={(e) => patchRow(r.key, { receiptNumber: e.target.value })}
                      disabled={locked}
                      maxLength={120}
                      placeholder="e.g. 004821A"
                    />
                  </label>

                  <div className="ntm-field ntm-f-receipt">
                    <span>Receipt</span>
                    {r.receipt === null ? (
                      <FileDrop
                        accept={RECEIPT_ACCEPT}
                        disabled={locked}
                        label="Drop or choose"
                        compact
                        onFiles={(files) => attachReceipt(r.key, files[0]!)}
                      />
                    ) : (() => {
                      // Attached: the cell shows what it WILL be stored as
                      // (the file's own name stops being the useful one) and
                      // opens it, so you can check you grabbed the right
                      // receipt before saving rather than after.
                      const name = previewReceiptFilename(
                        r.receipt,
                        receiptNameInput(r, caseNumber, tripDate),
                      );
                      return (
                        <div className="ntm-receipt-chip">
                          <button
                            type="button"
                            className="ntm-receipt-open"
                            // Both names: the one you dropped, so you can tell
                            // which receipt this row got, and the one it lands
                            // under, which is what the label shows.
                            title={`${r.receipt.name} — stored as ${name}`}
                            onClick={() => setPreview({
                              // Unused: `blob` short-circuits the fetch.
                              downloadUrl: '',
                              filename: name,
                              mimeType: r.receipt!.type || 'application/octet-stream',
                              blob: r.receipt!,
                            })}
                          >
                            <ReceiptIcon
                              mimeType={name.toLowerCase().endsWith('.pdf')
                                ? 'application/pdf'
                                : r.receipt.type}
                            />
                            <span className="ntm-receipt-name">{name}</span>
                          </button>
                          <IconAction
                            action="delete"
                            label="Remove this receipt"
                            iconSize={14}
                            disabled={locked}
                            onClick={() => patchRow(r.key, {
                              receipt: null, receiptPath: null, ocrPct: null,
                            })}
                          />
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {r.ocrPct !== null && (
                  <p className="ntm-hint">Reading receipt… {Math.round(r.ocrPct * 100)}%</p>
                )}
                {r.ocrFilled.length > 0 && (
                  <p className="ntm-hint">
                    From the receipt: {r.ocrFilled.join(', ')}.{' '}
                    <button type="button" className="ntm-link-btn" onClick={() => clearRowOcr(r.key)}>
                      Clear
                    </button>
                  </p>
                )}
                {r.error && <p className="ntm-error" role="alert">{r.error}</p>}
              </div>
            );
          })}

          <button
            type="button"
            className="btn-charcoal-secondary ntm-add"
            onClick={() => setRows((prev) => [...prev, blankCostRow()])}
            disabled={busy}
          >
            + Add cost
          </button>
        </div>

        {/* ---- travel time ---- */}
        <div className="ntm-section">
          <h3 className="ntm-section-title">Travel Time</h3>

          {projectId === GENERAL_BUSINESS ? (
            <p className="ntm-hint">
              Travel time bills against a project, so it isn't available for general-business
              trips. Pick a project above to add billable hours.
            </p>
          ) : projectId === 0 ? (
            <p className="ntm-hint">Pick a project above to bill travel hours.</p>
          ) : !can('time_entry.write') ? (
            <p className="ntm-hint">You don't have permission to log time.</p>
          ) : (
            <>
              <p className="ntm-hint">
                One leg per drive or flight — each becomes its own time entry and its own
                invoice line. Remove them all to record costs only.
              </p>

              {timeRows.map((t, i) => {
                const locked = busy || t.savedId !== null;
                const rate = rateFor(t.serviceRateId);
                return (
                  <div
                    key={t.key}
                    className={`ntm-time${t.savedId !== null ? ' ntm-saved' : ''}${t.error ? ' ntm-invalid' : ''}`}
                  >
                    <div className="ntm-time-head">
                      <label className="ntm-field ntm-f-rate" htmlFor={`${t.key}-rate`}>
                        <span>Service rate * {i === 0 ? '' : `(leg ${i + 1})`}</span>
                        <select
                          id={`${t.key}-rate`}
                          className="form-input"
                          value={t.serviceRateId}
                          onChange={(e) =>
                            patchTimeRow(t.key, { serviceRateId: Number(e.target.value) })}
                          disabled={locked || ratesQ.isPending}
                        >
                          <option value={0}>
                            {ratesQ.isPending ? 'Loading rates…' : 'Select a rate…'}
                          </option>
                          {rates.map((r) => (
                            <option key={r.id} value={r.id}>{rateOptionText(r)}</option>
                          ))}
                        </select>
                      </label>

                      <div className="ntm-cost-actions">
                        {t.savedId !== null ? (
                          <span className="ntm-saved-pill" title="Already logged">logged</span>
                        ) : (
                          <IconAction
                            action="delete"
                            label="Remove this leg"
                            iconSize={16}
                            disabled={busy}
                            onClick={() => setTimeRows((prev) =>
                              reseedDescriptions(prev.filter((x) => x.key !== t.key), purpose))}
                          />
                        )}
                      </div>
                    </div>

                    <TimeRangeFields
                      value={t.range}
                      onChange={(next) => patchTimeRow(t.key, { range: next })}
                      disabled={locked}
                      idPrefix={t.key}
                    />

                    <label className="ntm-field" htmlFor={`${t.key}-desc`}>
                      <span>Description *</span>
                      <textarea
                        id={`${t.key}-desc`}
                        className="form-input ntm-textarea"
                        value={t.description}
                        onChange={(e) =>
                          patchTimeRow(t.key, { description: e.target.value, descTouched: true })}
                        disabled={locked}
                        rows={2}
                        maxLength={10000}
                        placeholder={i === 1
                          ? 'Return drive from Richmond'
                          : 'Drive to Richmond for deposition'}
                      />
                    </label>

                    {rate && MONEY_RE.test(t.range.hours) && (
                      <p className="ntm-hint">
                        {t.range.hours} h × {formatMoney(rate.amount)} ={' '}
                        <strong>
                          {formatMoney((Number(t.range.hours) * Number(rate.amount)).toFixed(2))}
                        </strong>
                      </p>
                    )}
                    {t.error && <p className="ntm-error" role="alert">{t.error}</p>}
                  </div>
                );
              })}

              {!ratesQ.isPending && rates.length === 0 && (
                <p className="ntm-hint">
                  This project has no rates yet — add one under Resources → Rates.
                </p>
              )}

              <button
                type="button"
                className="btn-charcoal-secondary ntm-add"
                onClick={() => setTimeRows((prev) =>
                  reseedDescriptions([...prev, blankTimeRow(prev[prev.length - 1], tripDate)], purpose))}
                disabled={busy}
              >
                + Add travel time
              </button>
            </>
          )}
        </div>

        {topError && <p className="ntm-error-banner" role="alert">{topError}</p>}
      </form>

      {/* Nested inside this modal, which <Modal> supports via --z-modal-nested. */}
      {preview && (
        <DocumentPreviewModal file={preview} onClose={() => setPreview(null)} />
      )}
    </Modal>
  );
}
